// Types
import type {
  JobOperation,
} from 'fc-operator-sampling-jobs/src/types'

import type {
  Job,
} from 'fc-model-sampling-jobs/types'

// Libs
import _map from 'lodash/map'
import _every from 'lodash/every'
import _has from 'lodash/has'

// Operator
import operator from '../tableOperator'

import tableOperations$ from '../selectors/tableOperations'

// Controller
import controller from 'fc-ctrl-sampling-jobs'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'

import { firstAvailableGrowerId$ } from 'fc-assets'

type OperationsArgs = {
  operations$: (state: Object) => Array<JobOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.edit(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false
  while (!done) {
    const effect = yield race({
      submits: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit || effect.submits) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(tableOperations$)
  const nextOperations = yield _map(operations, function (operation: JobOperation): Object {
    return call(handleSubmitOperation, operation)
  })
  return _every(nextOperations, function (operation: JobOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: JobOperation): Generator<*, JobOperation, *> {
  const model: Job = operation.model
  const jobId = _has(model, 'id') ? model.id : undefined
  const growerId = yield select(firstAvailableGrowerId$)

  const proc = operation.operation === 'ADD'
    ? controller.create
    : operation.operation === 'EDIT'
      ? controller.update
      : controller.delete

  const { error, response } = yield call([controller, proc], {
    model: {...model},
    queryParams: {
      rootAsset: growerId,
    },
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)

    if (operation.operation === 'DELETE') {
      yield put(controller.actionCreators.deleted({id: jobId}))
    }

    yield put(action)

    return nextOperation
  }
}
